import { ActionTree } from 'vuex'
import { basePath, ModuleState } from './types'
import { RootState } from '@/types'
import { NewPost } from './types'
import { API, handleError } from '@/helpers/axios'
import { eventHub } from '@/store/eventHub'
import { Post } from '@/store/posts/types'
import i18next from 'i18next'
import i18nextConfig from '@/helpers/i18next-config'

i18next.init(i18nextConfig)

interface PostTownUserIds {
  bulk: boolean | null;
  postId: number;
  townId: number;
  userId: number;
}

interface PostsBulkDelete {
  townId: number;
  posts: {
    id: number;
    userId: number;
  }[];
}

interface PostsBulk {
  townId: number;
  posts: {
    id: number;
    user: { id: number; };
  }[];
}

interface Rejection {
  postId: number;
  userId: number;
  townId: number;
  reason: string;
  comment: string;
}

export interface TownUserStatus {
  townId: number;
  userId: number;
  status: string;
  limit: number | null;
  townPreferred?: boolean;
}

export const actions: ActionTree<ModuleState, RootState> = {
  async fetchBy({ commit }, { id, townId }: { id: number; townId: number } ) {
    try {
      const response = await API.get(`${basePath}/group/${townId}/posts/${id}`)
      if (response.status === 200) {
        commit('handleResponseFetchBy', response.data)
        return response.data
      } else {
        console.error(response)
        throw new Error('Error fetching post.')
      }
    }
    catch(error) { handleError(error) }
  },
  async fetchTownUserByStatus({ commit }, { townId, userId, status, limit, townPreferred = false }: TownUserStatus) {
    try {
      const limitNumber = typeof limit === 'number' ? limit : 2
      const response = await API
        .get(`${basePath}/group/${townId}/members/${userId}/posts/${status}`,
          { params: { limit: limitNumber, townPreferred } })

      if (response.status === 200) {
        switch (status) {
          case 'pending':
            commit('handleResponseByTownByUserPending', response.data.posts)
            break
          case 'active':
            commit('handleResponseByTownByUserActive', response.data.posts)
            break
          case 'inactive':
            commit('handleResponseByTownByUserInactive', response.data.posts)
            break
        }
        return response.data.posts
      }
    }
    catch(error) { handleError(error) }
	},
  async fetchPendingByTown({ commit }, townId: number) {
    try {
      const response = await API.get(`${basePath}/group/${townId}/posts/pending`)
      if (response.status === 200) {
        commit('handleFetchPendingByTownResponse', response.data)
        return response.data
      }
    }
    catch(error) { handleError(error) }
	},
  async approve({ dispatch }, { postId, townId, userId }: PostTownUserIds) {
    try {
      const response = await API.post(`${basePath}/group/${townId}/posts/approve`, { postId, userId })

      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: i18next.t(`Approved post.`),
          undo: () => dispatch('unApprove', { postId, userId, townId })
        })
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async bulkApprove({ dispatch }, { townId, posts }: PostsBulk) {
    try {
      const response = await API.post(`${basePath}/group/${townId}/posts/bulk/approve`, { posts })

      if(response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: posts.length + i18next.t(` posts approved.`),
          undo: () => dispatch('bulkUnApprove', { townId, posts })
        })
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async unApprove(_, { postId, townId, userId }: PostTownUserIds) {
    try {
      return await API.post(`${basePath}/group/${townId}/posts/unapprove`, { postId, userId })
    }
    catch(error) { handleError(error) }
  },
  async bulkUnApprove(_, { posts, townId }: PostsBulk) {
    try {
      return await API.post(`${basePath}/group/${townId}/posts/bulk/unapprove`, { posts })
    }
    catch(error) { handleError(error) }
  },
  async create({ dispatch }, { userId, subject, description, type, town, lendDuration, location, images }: NewPost) {
    try {
      const response = await API.post(`${basePath}/group/${town}/posts/create`,
                                      { userId, subject, description, type, town, lendDuration, location, images })
      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'success',
          text: `${i18next.t('Created post')} ${subject}.`,
          undo: () => dispatch('unReject', { postId: response.data, userId, town })
        })
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async reject({ dispatch }, { postId, userId, townId, reason, comment }: Rejection) {
    try {
      const response = await API
        .post(`${basePath}/group/${townId}/posts/${postId}/reject`, { reason, comment, userId })

      if (response.status === 200) {
        eventHub.$emit('triggerSnackbar', {
          color: 'warning',
          text: i18next.t(`Rejected post.`),
          undo: () => dispatch('unReject', { postId, userId, townId })
        })
      }
    }
    catch(error) { handleError(error) }
  },
  async unReject(_, {postId, townId, userId}: PostTownUserIds) {
    try {
      return await API.post(`${basePath}/group/${townId}/posts/${postId}/unreject`, { userId })
    }
    catch(error) { handleError(error) }
  },
  async delete({ dispatch }, { bulk, postId, townId, userId }: PostTownUserIds) {
    try {
      const response = await API
        .delete(`${basePath}/group/${townId}/posts/${postId}`, { params: { userId } })

      if (response.status === 200) {
        if (!bulk) {
          eventHub.$emit('triggerSnackbar', {
            color: 'warning',
            text: i18next.t(`Deleted post.`),
            undo: () => dispatch('unDelete', { postId, userId, townId })
          })
        }
      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  // TODO: Finish
  async bulkDelete({ dispatch }, { posts, townId }: PostsBulkDelete) {
    try {
      const response = await API
        .delete(`${basePath}/group/${townId}/posts/bulk`, { data: { posts } })

      if (response.status === 200) {
        // TODO: How to handl undo?
        // eventHub.$emit('triggerSnackbar', {
        //   color: 'warning',
        //   text: i18next.t(`Deleted posts.`),
        //   undo: () => dispatch('unDelete', { postId, userId, townId })
        // })

      }
      return response.data
    }
    catch(error) { handleError(error) }
  },
  async unDelete(_, { postId, townId, userId }: PostTownUserIds) {
    try {
      return await API.post(`${basePath}/group/${townId}/posts/${postId}/undelete`, { userId })
    }
    catch(error) { handleError(error) }
  },
  async update(_, { post }: { post: Post }) {
    try {
      const shimmedPost = Object.assign(post, {postType: post.type})
      return await API.post(`${basePath}/group/${post.group.id}/posts/${post.id}/update`, { post: shimmedPost })
    }
    catch (error) { handleError(error); return error }
  }
}
